import React, { useCallback, useRef, useMemo, useEffect, forwardRef } from 'react';
import { useDispatch } from 'react-redux';
import { Marker, Popup } from 'react-leaflet';
import { getMainUnitFromBundleMembers, getMaxAlarmForDevices, isPivot } from 'common/utils/helpers';
import { isItBundle } from 'common/utils/deviceTwinUtils';
import { Alarms } from 'common/utils/constants';
import { deviceIcon } from 'common/deviceMapIcons';
import { updateDevicesPageSelectedDeviceId } from 'redux/actions';
import { mergeRefs } from '../../utils';
import { DevicePopup } from '../DevicePopup';

const defaultLocation = {
  lat: 0,
  long: 0
};

const DeviceMarker = forwardRef(function DeviceMarker(
  { device: _device, bulkViewModalOpen, selected, open, alarm, handleMeasureDeviceOnClick, isMeasureModeOn },
  ref
) {
  const markerRef = useRef(null);
  const dispatch = useDispatch();
  const bundle = useMemo(() => (isItBundle(_device) ? _device : null), [_device]);
  const device = useMemo(() => (bundle ? getMainUnitFromBundleMembers(bundle.members) : _device), [bundle, _device]);
  const position = useMemo(() => {
    const { lat = defaultLocation.lat, long = defaultLocation.long } = device.properties?.reported?.location || {};

    return {
      lat,
      lng: long
    };
  }, [device]);
  const pivot = useMemo(() => isPivot(device), [device]);
  const deviceMaxAlarm = useMemo(
    () => getMaxAlarmForDevices(alarm ? (bundle ? bundle.members : [device]) : []),
    [alarm, bundle, device]
  );

  const divIcon = useMemo(
    () =>
      deviceIcon({
        deviceType: pivot ? 'pivot' : 'echo',
        alarmLevel: bulkViewModalOpen ? Alarms[deviceMaxAlarm] : null,
        selected: selected || open,
        className: ''
      }),
    [pivot, selected, open, bulkViewModalOpen, deviceMaxAlarm]
  );
  const handleClick = useCallback(() => {
    if (isMeasureModeOn) {
      handleMeasureDeviceOnClick(position);
    } else {
      dispatch(toggleSelectedDevice(bundle ? bundle.bundleId : device.deviceId));
    }
  }, [dispatch, device, bundle, isMeasureModeOn, handleMeasureDeviceOnClick, position]);

  useEffect(() => {
    if (markerRef.current) {
      const {
        current: { leafletElement }
      } = markerRef;

      if (!isMeasureModeOn) {
        if (open) {
          leafletElement.openPopup();
        } else {
          leafletElement.closePopup();
        }
      }
    }
  }, [open, isMeasureModeOn]);

  return (
    <Marker
      ref={mergeRefs(markerRef, ref)}
      position={position}
      icon={divIcon}
      device={device}
      bundle={bundle}
      onClick={handleClick}
    >
      {!isMeasureModeOn ? (
        <DevicePopup bundle={bundle} device={device} />
      ) : (
        <Popup position="top">
          <span style={{ font: '400 13 Rubik', color: 'var(--ibmsNavy)' }}>{bundle?.name || device?.deviceId}</span>
        </Popup>
      )}
    </Marker>
  );
});

export default DeviceMarker;

function toggleSelectedDevice(id) {
  return (dispatch, getState) => {
    const {
      ui: {
        devicesPage: { selectedDeviceId }
      }
    } = getState();

    dispatch(updateDevicesPageSelectedDeviceId(selectedDeviceId !== id ? id : null));
  };
}
